import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Users, Mail, Download, Search, Play, Pause, Lock, Filter, Loader2, Edit2 } from 'lucide-react';
import { crmMailingListsApi, CrmMailingList } from '../services/crmApi';
import { toast } from 'sonner';
export function MailingListDetail() {
  const {
    listId
  } = useParams<{
    listId: string;
  }>();
  const [list, setList] = useState<CrmMailingList | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'subscribers' | 'submissions'>('subscribers');
  useEffect(() => {
    const fetchList = async () => {
      if (!listId) return;
      setLoading(true);
      try {
        const data = await crmMailingListsApi.get(listId);
        setList(data);
      } catch (err) {
        console.error('Failed to fetch list:', err);
        toast.error('Failed to load mailing list details');
      } finally {
        setLoading(false);
      }
    };
    fetchList();
  }, [listId]);
  const getIntakeStatusConfig = (status: string) => {
    switch (status) {
      case 'open':
        return {
          icon: Play,
          color: 'text-green-600',
          bg: 'bg-green-50',
          label: 'Accepting Signups'
        };
      case 'paused':
        return {
          icon: Pause,
          color: 'text-yellow-600',
          bg: 'bg-yellow-50',
          label: 'Paused'
        };
      case 'closed':
        return {
          icon: Lock,
          color: 'text-red-600',
          bg: 'bg-red-50',
          label: 'Closed'
        };
      default:
        return {
          icon: Play,
          color: 'text-gray-600',
          bg: 'bg-gray-50',
          label: 'Unknown'
        };
    }
  };
  if (loading) {
    return <div className="flex items-center justify-center py-24" data-id="element-3247">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-3248" />
        <span className="ml-3 text-sm text-gray-500" data-id="element-3249">
          Loading list details...
        </span>
      </div>;
  }
  if (!list) {
    return <div className="text-center py-12" data-id="element-3250">
        <h2 className="text-lg font-medium text-gray-900" data-id="element-3251">
          Mailing list not found
        </h2>
        <Link to="/mailing-lists" className="text-blue-600 hover:text-blue-800 mt-2 inline-block" data-id="element-3252">
          ← Back to Mailing Lists
        </Link>
      </div>;
  }
  const intakeConfig = getIntakeStatusConfig(list.Status);
  const IntakeIcon = intakeConfig.icon;
  return <div className="space-y-6" data-id="element-3253">
      {/* Header */}
      <div className="flex items-start justify-between" data-id="element-3254">
        <div className="flex items-start gap-4" data-id="element-3255">
          <Link to="/mailing-lists" className="p-2 hover:bg-gray-100 rounded-full text-gray-500 mt-1" data-id="element-3256">
            <ArrowLeft className="h-5 w-5" data-id="element-3257" />
          </Link>
          <div data-id="element-3258">
            <div className="flex items-center gap-3" data-id="element-3259">
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3260">
                {list.Name}
              </h1>
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${intakeConfig.bg} ${intakeConfig.color}`} data-id="element-3261">
                <IntakeIcon className="h-3 w-3" data-id="element-3262" />
                {intakeConfig.label}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1" data-id="element-3263">
              {list.Description || 'No description'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-3264">
          <button className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-3265">
            <Edit2 className="h-4 w-4" data-id="element-3266" />
            Edit
          </button>
          <button className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-3267">
            <Download className="h-4 w-4" data-id="element-3268" />
            Export
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4" data-id="element-3269">
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-3270">
          <div className="flex items-center gap-3" data-id="element-3271">
            <div className="p-2 bg-blue-50 rounded-lg" data-id="element-3272">
              <Users className="h-5 w-5 text-blue-600" data-id="element-3273" />
            </div>
            <div data-id="element-3274">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-3275">
                {(list.Subscriber_Count || 0).toLocaleString()}
              </p>
              <p className="text-xs text-gray-500" data-id="element-3276">Total Subscribers</p>
            </div>
          </div>
        </div>
        {/* Placeholder stats for now since we don't have subscriber details from CRM yet */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 opacity-50" data-id="element-3277">
          <div className="flex items-center gap-3" data-id="element-3278">
            <div className="p-2 bg-green-50 rounded-lg" data-id="element-3279">
              <Mail className="h-5 w-5 text-green-600" data-id="element-3280" />
            </div>
            <div data-id="element-3281">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-3282">-</p>
              <p className="text-xs text-gray-500" data-id="element-3283">Confirmed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-3284">
        <nav className="-mb-px flex gap-6" data-id="element-3285">
          <button onClick={() => setActiveTab('subscribers')} className={`py-3 px-1 border-b-2 text-sm font-medium ${activeTab === 'subscribers' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-3286">
            Subscribers
          </button>
          <button onClick={() => setActiveTab('submissions')} className={`py-3 px-1 border-b-2 text-sm font-medium ${activeTab === 'submissions' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-3287">
            Form Submissions
          </button>
        </nav>
      </div>

      {/* Content Placeholder */}
      <div className="bg-white border border-gray-200 rounded-lg p-12 text-center" data-id="element-3288">
        <Users className="mx-auto h-12 w-12 text-gray-400" data-id="element-3289" />
        <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-3290">
          Subscriber data syncing...
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-3291">
          Detailed subscriber lists are being synced from Zoho CRM.
        </p>
      </div>
    </div>;
}